import { Check, createElement } from "lucide";
import { twJoin } from "tailwind-merge";
import Editor from "~/js/editor";
import { PromptHelper } from "~/js/graph/promptHelper";
import { customStructuredClone } from "~/js/utilities/customStructuredClone";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { $div } from "~/js/utilities/TurboDom";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { buttonStyles, inputStyles } from "~/solidJs/shared";
import {
	ArrayHelpers,
	onNextFrame,
	preventResrverdKeyEvent,
	recalculateTextAreaHeight
} from "~/solidJs/shared/helpers";
import { setDataAtrOnlyInTests } from "~/solidJs/shared/helpers/testHelpers";
import { validateIntAcordAccessLimit } from "~/solidJs/shared/helpers/validationHelpers";
import { openEnumContextMenu } from "~/solidJs/widgets/EnumContextMenu";
import { NodeEvents, SocketEvents } from "../types/IEventorListners";
import { StyleParam } from "../utilities/components/styleParam";
import Eventor from "../utilities/Eventor";
import { TextSelector, TextSelectorManager } from "../utilities/textSelector";

/**
 * Custom Checkbox UI component
 */
class CustomCheckbox {
	constructor(options = {}) {
		const {
			testType = "",
			testName = "",
			className = "",
			onChange = () => {},
			onMouseDown = () => {}
		} = options;

		this.onChange = onChange;
		this.onMouseDown = onMouseDown;

		// Create container for checkbox
		this.container = document.createElement("label");
		this.container.className = `flex items-center cursor-pointer select-none relative ${className}`;

		// Create hidden input
		this.input = document.createElement("input");
		this.input.type = "checkbox";
		this.input.className = "sr-only"; // Hide the default checkbox

		if (testType && testName) {
			setDataAtrOnlyInTests(() => {
				this.input.setAttribute("data-test", `nodeParam${testType}`);
				this.input.setAttribute("data-test-name", `nodeParam${testName}`);
			});
		}

		// Create custom checkbox visual
		this.customCheckbox = document.createElement("div");
		this.customCheckbox.className =
			"relative w-4 h-4 rounded border border-gray-300 bg-background-gray-light flex items-center justify-center transition-all duration-200 hover:border-gray-400";

		// Create confirm icon (initially hidden)
		this.confirmIcon = createElement(Check, {
			height: 12,
			width: 12,
			class: "opacity-0 transition-opacity duration-200"
		});

		this.customCheckbox.appendChild(this.confirmIcon);
		this.container.appendChild(this.input);
		this.container.appendChild(this.customCheckbox);

		// Setup event listeners
		this.input.addEventListener("change", () => {
			this.setValue(this.input.checked);
			this.onChange(this.input.checked);
		});

		this.container.addEventListener("mousedown", (e) => {
			e.stopPropagation();
			this.onMouseDown(e);
		});
	}

	updateVisualState(checked) {
		if (checked) {
			this.customCheckbox.className =
				"relative w-4 h-4 rounded border border-blue-500 bg-blue-500 flex items-center justify-center transition-all duration-200 hover:border-blue-600 hover:bg-blue-600";
			this.confirmIcon.setAttribute(
				"class",
				"ml-[-1px] w-3 h-3 opacity-100 transition-opacity duration-200 text-white"
			);
		} else {
			this.customCheckbox.className =
				"relative w-4 h-4 rounded border border-gray-300 flex items-center justify-center transition-all duration-200 hover:border-gray-400 bg-background-gray-light";
			this.confirmIcon.setAttribute(
				"class",
				"ml-[-1px] w-3 h-3 opacity-0 transition-opacity duration-200 text-white"
			);
		}
	}

	get domElement() {
		return this.container;
	}

	getValue() {
		return this.checked;
	}

	setValue(value) {
		this.checked = value;
		this.input.checked = value;
		this.updateVisualState(value);
	}

	addClass(className) {
		this.container.classList.add(className);
	}

	addEventListener(event, handler) {
		this.input.addEventListener(event, handler);
	}
}

async function dbModelModifier(options, selectedIndex, prevIndex) {
	if (selectedIndex === prevIndex) {
		return;
	}
	const selectedModel = options[selectedIndex] || options[0];
	const promptSocket =
		this.node.inputSocketGroup.socketHashMap.get("text_prompt") ||
		this.node.inputSocketGroup.socketHashMap.get("description");
	if (!promptSocket) return;
	let socketValue = await promptSocket.tryGetValue();
	if (prevIndex !== undefined) {
		const prevSelectedModel = options[prevIndex];
		if (prevSelectedModel?.value) {
			socketValue = socketValue.replace(`${prevSelectedModel.name},`, "").trim();
		}
	}
	if (!selectedModel.value) {
		promptSocket.setValue(socketValue);
		this.node.inputSocketGroupModifiers.deleteAllSocketModifications(this.name);
		return;
	}
	promptSocket.setValue(`${selectedModel.name}, ${socketValue}`);
	this.node.inputSocketGroupModifiers.addModification(this.name, {
		fromSocketName: this.node.inputSocketGroup.socketHashMap.has("text_prompt")
			? "text_prompt"
			: "description",
		value: [`${selectedModel.name}, _____`]
	});
}

/**
 * Processes raw option data into a standardized format
 * @param {Array} rawOptions - Raw options from various sources
 * @param {boolean} checkAccess - Whether to check group access
 * @returns {Array} Processed options with consistent structure
 */
function processEnumOptions(rawOptions = [], checkAccess = true) {
	return rawOptions.map((opt) => {
		if (typeof opt === "string") {
			return {
				value: opt,
				name: opt,
				disabled: false
			};
		}

		const hasGroupAccess =
			!checkAccess ||
			!opt.hasAccessGroupMap ||
			getUserStore()?.group in opt.hasAccessGroupMap;

		return {
			value: opt.value,
			name: opt.name,
			disabled: opt.disabled || !hasGroupAccess,
			modifiers: opt.modifiers,
			...opt
		};
	});
}

/**
 * Creates enum button element with proper styling and content
 * @param {string} type - Input type for data attributes
 * @param {string} name - Input name for data attributes
 * @returns {Object} Button element and text span
 */
function createEnumButton(type, name) {
	const button = document.createElement("button");
	button.type = "button";
	button.className = buttonStyles({
		color: "TertiaryOverlay",
		size: "small",
		class: twJoin(
			"enum-select-button !text-2xs min-w-[90px] max-w-[150px] shrink-0 justify-between overflow-hidden text-ellipsis whitespace-nowrap px-2 py-1"
		)
	});

	const textSpan = document.createElement("span");
	textSpan.className = "text-ellipsis overflow-hidden shrink-1";
	button.appendChild(textSpan);

	setDataAtrOnlyInTests(() => {
		button.setAttribute("data-test", `nodeParam${type}`);
		button.setAttribute("data-test-name", `nodeParam${name}`);
	});

	return { button, textSpan };
}

/**
 * Updates button text based on current selection
 * @param {HTMLElement} textSpan - Text span element to update
 * @param {Array} options - Available options
 * @param {number} selectedIndex - Currently selected index
 * @returns {*} Current value
 */
function updateEnumButtonText(textSpan, options, selectedIndex) {
	if (options.length > 0 && selectedIndex < options.length) {
		const selectedOption = options[selectedIndex];
		textSpan.textContent = selectedOption.name;
		return selectedOption.value;
	}
	if (options.length > 0) {
		const selectedOption = options[0];
		textSpan.textContent = selectedOption.name;
		return selectedOption.value;
	}
	textSpan.textContent = "Loading...";
	return null;
}

/**
 * Sets up event listeners for enum button
 * @param {HTMLElement} button - Button element
 * @param {Object} context - Context object with socket, node, etc.
 * @param {Function} openMenuFn - Function to open context menu
 */
function setupEnumEventListeners(button, context, openMenuFn) {
	button.addEventListener("click", openMenuFn);
	button.addEventListener("mousedown", (event) => {
		event.stopPropagation();
		Editor.context.graph.handleNodeSelection(context.node, event);
	});
}

/**
 * Handles option selection and related side effects
 * @param {Object} context - Context object
 * @param {Array} options - Available options
 * @param {number} newIndex - New selected index
 * @param {number} prevIndex - Previous selected index
 */
function handleEnumSelection(context, options, newIndex, prevIndex) {
	context.node.inputSocketGroupModifiers.deleteAllSocketModifications(context.name);

	context.eventor.invoke("ga-change");
	context.eventor.invoke("change");

	if (context.optionalInfo.isNeedToFetchModels || context.optionalInfo.isNeedToFetchLoras) {
		dbModelModifier.call(context, options, newIndex, prevIndex);
	}

	context.applyModifiers(options[newIndex]?.modifiers);
}

/**
 * Loads and processes models data
 * @param {Object} context - Context object
 * @param {Object} initialOptions - Initial options to merge with
 * @returns {Promise<Object>} Processed options and metadata
 */
async function loadModelsData(context, initialOptions) {
	const allModels = await context.node.myModels;
	const models = allModels?.[context.optionalInfo.model_type];
	let isNeedToShowDisabledText = false;
	const clonedOptions = customStructuredClone(initialOptions);

	let processedOptions = processEnumOptions(initialOptions.values || []);

	if (models && !context.socket.accessDisabled) {
		const modelOptions = models.map((model) => ({
			...model,
			disabled: model.isDisabled
		}));

		isNeedToShowDisabledText = modelOptions.some((model) => model.disabled);
		processedOptions = processedOptions.concat(processEnumOptions(modelOptions, false));
		clonedOptions.values.push(...modelOptions);
	}

	return {
		options: ArrayHelpers.removeDuplicates(processedOptions, (item) => item.value),
		clonedOptions,
		isNeedToShowDisabledText
	};
}

/**
 * Loads and processes LoRA data
 * @param {Object} context - Context object
 * @param {Object} initialOptions - Initial options to merge with
 * @returns {Promise<Object>} Processed options and metadata
 */
async function loadLorasData(context, initialOptions) {
	const models = await context.node.myLoras;
	let isNeedToShowDisabledText = false;
	const clonedOptions = customStructuredClone(initialOptions);

	let processedOptions = processEnumOptions(initialOptions.values || []);

	if (models && !context.socket.accessDisabled) {
		const modelOptions = models.map((model) => ({
			...model,
			disabled: model.isDisabled
		}));

		isNeedToShowDisabledText = modelOptions.some((model) => model.disabled);
		processedOptions = processedOptions.concat(processEnumOptions(modelOptions, false));
		clonedOptions.values.push(...modelOptions);
	}

	return {
		options: ArrayHelpers.removeDuplicates(processedOptions, (item) => item.value),
		clonedOptions,
		isNeedToShowDisabledText
	};
}

/**
 * Creates context menu opener function
 * @param {Object} context - Context object
 * @param {Function} getOptions - Function to get current options
 * @param {Function} getCurrentValue - Function to get current value
 * @param {Function} closeCallback - Callback for closing menu
 * @returns {Function} Menu opener function
 */
function createMenuOpener(context, getOptions, getCurrentValue, closeCallback, button) {
	return (event) => {
		event.preventDefault();
		event.stopPropagation();

		// Check if menu is already open
		if (button.getAttribute("data-open") === "true") {
			return;
		}

		const options = getOptions();
		if (context.socket.accessDisabled || options.length === 0) {
			return;
		}

		// Set data-open attribute to true
		button.setAttribute("data-open", "true");

		const rect = button.getBoundingClientRect();
		const pos = Editor.context.graph.navigation.clientToGraphSpace(rect.left, rect.top);
		const position = {
			left: pos.x,
			top: pos.y + 15
		};

		openEnumContextMenu(
			context.socket.name,
			context.node.uuid,
			position,
			options,
			(value) => context.setValue(value),
			getCurrentValue(),
			closeCallback
		);
	};
}

export default class DataEntry {
	eventor = new Eventor();
	/**
	 * @type {NodeType}
	 */
	node;
	/**
	 *
	 * @param {*} type
	 * @param {*} value
	 * @param {*} options
	 * @param {Node} node
	 * @param {*} name
	 * @param {*} optionalInfo
	 * @param {*} socket
	 */
	constructor(type, value, options, node, name, optionalInfo, socket) {
		this.node = node;
		this.type = type;
		this.name = name;
		this.optionalInfo = optionalInfo;
		/**
		 * @type {Socket}
		 */
		this.socket = socket;

		this.dom = {};
		this.dom.dataEntry = this.getDataEntryElement(type, value, options, name);
	}

	addListener = (eventName, listener) => this.eventor.addListener(eventName, listener);
	removeListener = (eventName, listener) => this.eventor.removeListener(eventName, listener);

	/**
	 * Creates base UI structure for text inputs
	 * @param {HTMLElement} input - The input element to wrap
	 * @param {string} title - Title for the input
	 * @returns {Object} Container elements and utility methods
	 */
	createBaseTextInputStructure = (input, title, placeholder) => {
		input.placeholder = placeholder;
		const mainContainer = $div("mainContainer");
		const inputTitle = $div("inputTitle", mainContainer);
		inputTitle.textContent = title;

		const domElement = $div("generatePromptContainer", mainContainer);
		const iconContainer = $div("iconContainer", domElement);
		const errorContainer = $div("errorContainer", domElement);

		domElement.append(input);

		// Hide error by default
		this.hideError(errorContainer, domElement);

		return {
			mainContainer,
			inputTitle,
			domElement,
			iconContainer,
			errorContainer,
			showError: (msg) => this.showError(msg, errorContainer, domElement),
			hideError: () => this.hideError(errorContainer, domElement)
		};
	};

	/**
	 * Shows error message
	 * @param {string} msg - Error message
	 * @param {HTMLElement} errorContainer - Error container element
	 * @param {HTMLElement} domElement - Main dom element
	 */
	showError = (msg, errorContainer, domElement) => {
		domElement.classList.add("error");
		errorContainer.textContent = msg;
		errorContainer.classList.remove("hidden");
	};

	/**
	 * Hides error message
	 * @param {HTMLElement} errorContainer - Error container element
	 * @param {HTMLElement} domElement - Main dom element
	 */
	hideError = (errorContainer, domElement) => {
		domElement.classList.remove("error");
		errorContainer.classList.add("hidden");
	};

	/**
	 * Sets the title of the input
	 * @param {string} title - New title text
	 */
	setTitle = (title) => {
		if (this.dom.uiStructure?.inputTitle) {
			this.dom.uiStructure.inputTitle.textContent = title;
		}
	};

	/**
	 * Shows or hides the loading state
	 * @param {boolean} isLoading - Whether to show loading state
	 */
	toggleLoading = (isLoading) => {
		if (this.dom.uiStructure?.domElement) {
			this.dom.uiStructure.domElement.classList.toggle("loading", isLoading);
		}
	};

	/**
	 * Adds a CSS class to the main container
	 * @param {string} className - CSS class to add
	 */
	addContainerClass = (className) => {
		if (this.dom.uiStructure?.mainContainer) {
			this.dom.uiStructure.mainContainer.classList.add(className);
		}
	};

	/**
	 * Removes a CSS class from the main container
	 * @param {string} className - CSS class to remove
	 */
	removeContainerClass = (className) => {
		if (this.dom.uiStructure?.mainContainer) {
			this.dom.uiStructure.mainContainer.classList.remove(className);
		}
	};

	getDataEntryElement = (type, value, options, name) => {
		let input;
		switch (type) {
			case "text": {
				this.domElementName = "textarea";
				input = document.createElement("textarea");
				let inputValue = "";
				setDataAtrOnlyInTests(() => {
					input.setAttribute("data-test", `nodeParam${type}`);
					input.setAttribute("data-test-name", `nodeParam${name}`);
				});
				if (name) input.setAttribute("class", name);

				if (name === "negative_prompt") {
					input.placeholder = "Enter any details you do not want on your image";
				}

				// Create base UI structure
				const title = this.socket.definition.getName();
				const placeholder = this.socket.definition.getPlaceholder();
				const uiStructure = this.createBaseTextInputStructure(input, title, placeholder);
				this.dom.uiStructure = uiStructure;

				input.addEventListener("input", (e) => {
					if (this.socket.isConnected) {
						e.preventDefault();
						input.value = inputValue;
						return;
					}
					recalculateTextAreaHeight(e.target);
					inputValue = e.value;
				});
				Editor.context.graph.textAreasObserver.observe(input);
				input.addEventListener("mousedown", (e) => {
					e.stopPropagation();
					Editor.context.graph.handleNodeSelection(this.node, e);
				});
				input.addEventListener("dragstart", (e) => {
					e.stopPropagation();
					e.preventDefault();
				});
				input.addEventListener("change", () => {
					this.eventor.invoke("change", input.value);
					this.eventor.invoke("ga-change", input.value);
				});
				input.addEventListener("contextmenu", (e) => {
					e.stopPropagation();
				});
				input.addEventListener("keydown", (event) => {
					preventResrverdKeyEvent(event);
				});
				if (!value) value = "";
				input.value = `${value}`;
				onNextFrame(() => recalculateTextAreaHeight(input, 32));
				this.socket.addListener(SocketEvents.ConnectionChange, async (_value) => {
					const val = await this.socket.tryGetValue();
					this.setValue(val || "");
					this.socket.fireChange();
				});
				this.getValue = () => input.value.trim();
				this.setValue = (v) => {
					input.value = v;
					inputValue = v;
					recalculateTextAreaHeight(input);
				};
				this.setPlaceholder = (value) => {
					input.placeholder = value;
				};
				if (this.optionalInfo.isNeedToHideName) {
					this.setPlaceholder(
						this.socket.definition.getPlaceholder() || this.socket.definition.getName()
					);
				}

				// Text selector setup
				const textSelector = new TextSelector(
					this.optionalInfo.isNeedToHelp ? "selected" : "default"
				);
				textSelector.onTextChange(value);
				new TextSelectorManager(this.node, this.socket, input, textSelector);

				if (this.optionalInfo.isNeedToHelp) {
					this.genPrompt = new PromptHelper(
						input,
						this.node.globalId,
						() => this.node.getPromptHelperSource,
						this.socket,
						uiStructure // Pass the existing UI structure
					);
					this.genPrompt.Dom.domElement.prepend(textSelector.domElement());

					this.node.eventor.addListener(NodeEvents.PreExecutePreparing, () => {
						if (!input.value) {
							this.genPrompt.replaceValueWithPrompt();
							return;
						}
					});
					return this.genPrompt.domElement();
				}

				// Add text selector to the UI structure for regular text inputs
				setTimeout(() => {
					uiStructure.domElement.prepend(textSelector.domElement());
				});

				return uiStructure.mainContainer;
			}
			case "number": {
				this.domElementName = "custom";

				// Create the container for positioning
				const numberInputContainer = document.createElement("div");
				numberInputContainer.className =
					"relative w-full flex gap-1 items-center justify-center";

				input = document.createElement("input");
				input.className = inputStyles({
					color: "Gray",
					class: "border-none py-1 px-2 pr-8 leading-normal focus:ring-interaction-grey focus:ring-[1px] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] w-full",
					size: "small"
				});
				setDataAtrOnlyInTests(() => {
					input.setAttribute("data-test", `nodeParam${type}`);
					input.setAttribute("data-test-name", `nodeParam${name}`);
				});

				// Create buttons container positioned absolutely
				const buttonsContainer = document.createElement("div");
				buttonsContainer.className =
					"absolute right-0 top-0 bottom-0 flex flex-col gap-[1px] rounded-r-[6px] overflow-hidden";
				const buttonClass =
					"bg-background-gray hover:bg-background-interaction-tertiary outline-[none] border-none flex items-center justify-center w-[14px] flex-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors duration-150 rounded-none p-0";

				// Create increase button (top) - takes half height with top-right radius
				const increaseButton = document.createElement("button");
				increaseButton.type = "button";
				increaseButton.className = twJoin(buttonClass, "button-incr rounded-tr-[4px]");
				// Create decrease button (bottom) - takes half height with bottom-right radius
				const decreaseButton = document.createElement("button");
				decreaseButton.type = "button";
				decreaseButton.className = twJoin(buttonClass, "button-decr rounded-br-[4px]");
				// Assemble the buttons container
				buttonsContainer.appendChild(increaseButton);
				buttonsContainer.appendChild(decreaseButton);

				// Assemble the main container
				numberInputContainer.appendChild(input);
				numberInputContainer.appendChild(buttonsContainer);

				const numbersAfterZero = options.step.toString().split(".")[1]
					? options.step.toString().split(".")[1].length
					: 0;
				const validate = (validateValue) => {
					validateValue = Number(validateValue);
					if (Number.isNaN(validateValue)) {
						return options.min;
					}
					const units = validateValue / options.step;

					validateValue = (Math.round(units) * options.step).toFixed(numbersAfterZero);
					// validate accord min max
					const clampedValue = Math.min(
						Math.max(validateValue, options.min),
						options.max
					);

					if (this.optionalInfo.intLimitsAccessGroup) {
						const { isAvailable, ...rest } = validateIntAcordAccessLimit(
							clampedValue,
							getUserStore()?.group,
							this.optionalInfo.intLimitsAccessGroup
						);
						this.socket.layout
							.domElement()
							.classList.toggle("intAccessDisabled", !isAvailable);
						if (!isAvailable) {
							this.node.togglePremiumIntBanner({
								displayName: this.socket.definition.getName(),
								name: this.socket.name,
								requestedValue: clampedValue
							});
							if (rest.lastAvailableValue) {
								return rest.lastAvailableValue;
							}
						} else {
							this.node.layout.closePremiumIntBanner();
						}
					}
					return clampedValue;
				};
				input.addEventListener("change", (e) => {
					e.target.value = validate(e.target.value);
					this.eventor.invoke("change");
					this.eventor.invoke("ga-change");
				});
				input.addEventListener("mousedown", (e) => {
					e.stopPropagation();
					Editor.context.graph.handleNodeSelection(this.node, e);
				});
				input.addEventListener("dragstart", (e) => {
					e.stopPropagation();
					e.preventDefault();
				});
				input.type = "number";
				input.step = options.step;
				input.min = options.min;
				if (options.max) {
					input.max = options.max;
				}

				if (options.random) {
					const isFixedSeed = new CustomCheckbox({
						className: "isFixedSeed"
					});
					this.dom.isFixedSeed = isFixedSeed.domElement;

					this.generateRandomValue = () =>
						Number(
							Math.floor(
								(Math.random() * (options.max - options.min) + options.min) / 10
							)
						);

					if (this.optionalInfo.isFixedSeed) {
						input.value = value;
						isFixedSeed.setValue(true);
					} else {
						isFixedSeed.setValue(false);

						const seed = this.generateRandomValue();
						input.value = seed;
						this.optionalInfo.value = seed;
						this.socket.value = seed;
					}
				} else {
					input.value = validate(value);
				}

				input.oncontextmenu = (e) => {
					e.stopPropagation();
				};

				// Add functionality to decrease button
				decreaseButton.addEventListener("click", (e) => {
					e.preventDefault();
					e.stopPropagation();
					const currentValue = Number(input.value) || 0;
					const newValue = currentValue - options.step;
					const validatedValue = validate(newValue);
					input.value = validatedValue;
					this.eventor.invoke("change");
					this.eventor.invoke("ga-change");
				});

				// Add functionality to increase button
				increaseButton.addEventListener("click", (e) => {
					e.preventDefault();
					e.stopPropagation();
					const currentValue = Number(input.value) || 0;
					const newValue = currentValue + options.step;
					const validatedValue = validate(newValue);
					input.value = validatedValue;
					this.eventor.invoke("change");
					this.eventor.invoke("ga-change");
				});

				if (this.socket.getIsModificationEnabled()) {
					setTimeout(() => {
						this.eventor.invoke("change");
					});
				}

				this.getValue = () => validate(input.value);
				this.setValue = (value) => {
					value = validate(value);
					input.value = value;
					return value;
				};

				return numberInputContainer;
			}
			case "enum": {
				this.domElementName = "custom";

				// Create button with helper function
				const { button: input, textSpan } = createEnumButton(type, name);

				let options_data = [];
				let currentValue = null;
				let selectedIndex = 0;

				// Context object for passing to helper functions
				const enumContext = {
					node: this.node,
					socket: this.socket,
					name: this.name,
					optionalInfo: this.optionalInfo,
					eventor: this.eventor,
					applyModifiers: this.applyModifiers.bind(this),
					setValue: null // Will be set later
				};

				// Helper functions using context
				const updateButtonText = () => {
					currentValue = updateEnumButtonText(textSpan, options_data, selectedIndex);
				};

				const getCurrentValue = () => currentValue;
				const getOptions = () => options_data;

				// Create menu opener
				const openMenu = createMenuOpener(
					enumContext,
					getOptions,
					getCurrentValue,
					() => {
						onNextFrame(() => input.setAttribute("data-open", "false"));
					},
					input
				);

				this.socket.eventor.addListener(SocketEvents.OpenSelect, (event) => {
					openMenu(event);
				});
				// Setup event listeners
				setupEnumEventListeners(input, enumContext, openMenu);
				const reselectOption = (isEnabledModifications) => {
					const valueIndex = options_data.findIndex((opt) => opt.value === value);
					selectedIndex = valueIndex >= 0 ? valueIndex : 0;
					this.applyModifiers(
						options_data[selectedIndex]?.modifiers,
						isEnabledModifications
					);
					updateButtonText();
				};
				// Common callback for after data is fetched
				const afterFetchCallback = () => {
					const isEnabledModifications = this.socket.getIsModificationEnabled();
					reselectOption(isEnabledModifications);
					setTimeout(() => {
						if (!this.socket.isEnabled) {
							this.socket.node.eventor.invoke("myModalsWasFetched");
							return;
						}
						if (isEnabledModifications) {
							this.eventor.invoke("change");
						}
						this.applyModifiers(
							options_data[selectedIndex]?.modifiers,
							isEnabledModifications
						);
						dbModelModifier.call(this, options_data, selectedIndex, selectedIndex);
						this.socket.node.eventor.invoke("myModalsWasFetched");
					}, 0);
				};

				// Handle different data loading scenarios
				if (this.optionalInfo.isNeedToFetchModels) {
					input.classList.add("pending");
					textSpan.textContent = "Loading models...";

					loadModelsData(enumContext, options)
						.then(
							({
								options: processedOptions,
								clonedOptions,
								isNeedToShowDisabledText
							}) => {
								options_data = processedOptions;
								options = clonedOptions;

								if (isNeedToShowDisabledText) {
									this.socket.layout.toggleDisabledModelsText(true);
								}

								input.classList.remove("pending");
							}
						)
						.then(afterFetchCallback);
				} else if (this.optionalInfo.isNeedToFetchLoras) {
					input.classList.add("pending");
					textSpan.textContent = "Loading LoRAs...";

					loadLorasData(enumContext, options)
						.then(
							({
								options: processedOptions,
								clonedOptions,
								isNeedToShowDisabledText
							}) => {
								options_data = processedOptions;
								options = clonedOptions;

								if (isNeedToShowDisabledText) {
									this.socket.layout.toggleDisabledModelsText(true);
								}

								input.classList.remove("pending");
							}
						)
						.then(afterFetchCallback);
				} else {
					// Handle regular enum options
					options_data = ArrayHelpers.removeDuplicates(
						processEnumOptions(options.values || []),
						(item) => item.value
					);
					const isEnabledModifications = this.socket.getIsModificationEnabled();
					setTimeout(() => reselectOption(isEnabledModifications), 0);
				}

				// Replace options function
				this.replaceOptions = (newOptions) => {
					options_data = ArrayHelpers.removeDuplicates(
						processEnumOptions(newOptions.values || []),
						(item) => item.value
					);

					// Update selected index if current value still exists
					const valueIndex = options_data.findIndex((opt) => opt.value === currentValue);
					selectedIndex = valueIndex >= 0 ? valueIndex : 0;
					updateButtonText();

					const isModificationEnabled = this.socket.getIsModificationEnabled();
					setTimeout(() => {
						this.applyModifiers(
							options_data[selectedIndex]?.modifiers,
							isModificationEnabled
						);
						if (isModificationEnabled) {
							this.eventor.invoke("change");
						}
					}, 0);
				};

				// Value functions
				this.getValue = () => {
					return currentValue || (options_data.length > 0 ? options_data[0].value : null);
				};

				this.setValue = (value) => {
					const tempSelectedIndex = selectedIndex;
					const valueIndex = options_data.findIndex((opt) => opt.value === value);

					if (valueIndex >= 0) {
						selectedIndex = valueIndex;
						currentValue = value;
						updateButtonText();

						handleEnumSelection(
							enumContext,
							options_data,
							selectedIndex,
							tempSelectedIndex
						);
					}
				};

				// Set setValue in context for menu callback
				enumContext.setValue = this.setValue;

				this.close = () => {
					input.setAttribute("data-open", "false");
				};

				return input;
			}
			case "bool": {
				this.domElementName = "custom";

				const customCheckbox = new CustomCheckbox({
					testType: type,
					testName: name,
					onChange: (checked) => {
						this.eventor.invoke("change", checked);
						this.eventor.invoke("ga-change");
					},
					onMouseDown: (e) => {
						Editor.context.graph.handleNodeSelection(this.node, e);
					}
				});

				customCheckbox.setValue(value);

				this.getValue = () => customCheckbox.getValue();
				this.setValue = (value) => customCheckbox.setValue(value);

				return customCheckbox.domElement;
			}

			case "modifier": {
				this.domElementName = "custom";
				if (this.name === "style") {
					this.domElementName = "styleParam";
					const param = new StyleParam({
						socket: this.socket,
						node: this.node,
						optionalInfo: this.optionalInfo
					});
					this.getValue = param.getValue.bind(param);
					this.setValue = param.setValue.bind(param);
					return param.getDomElement();
				}
				const parentDataEntry = this;
				const defaultOption = options.find((_option) => _option.name === "default");
				const uuid = parentDataEntry.node.uuid.slice(0, 7);

				const container = document.createElement("div");
				const defaultContainer = document.createElement("div");
				this.radioContainer = document.createElement("div");
				let _artMode = false;
				let _artModeType = false;

				container.setAttribute("id", "div_");
				defaultContainer.setAttribute("class", "artistic-mode-default");
				this.radioContainer.setAttribute("class", "artistic-mode-radio");
				if (!this.socket.value) {
					this.radioContainer.style.display = "none";
				}

				if (defaultOption) {
					this.defaultCheckbox = document.createElement("input");
					setDataAtrOnlyInTests(() => {
						this.defaultCheckbox.setAttribute("data-test", `nodeParam${type}`);
						this.defaultCheckbox.setAttribute("data-test-name", `nodeParam${name}`);
					});
					this.defaultCheckbox.setAttribute("type", "checkbox");
					this.defaultCheckbox.checked = this.socket.value;
					this.defaultCheckbox.name = `default_${uuid}`;
					const label = document.createElement("labe");
					label.htmlFor = `default_${uuid}`;
					defaultContainer.appendChild(this.defaultCheckbox);
					defaultContainer.appendChild(label);

					this.defaultCheckbox.addEventListener("change", (e) => {
						this.socket.value = e.target.checked;

						EventAnalytics.artisticModeSelect(e.target.checked);
						if (e.target.checked) {
							parentDataEntry.applyModifiers(defaultOption);
							parentDataEntry.radioContainer.style.display = "block";
							_artMode = e.target.value;
						} else {
							parentDataEntry.radioContainer.style.display = "none";
							for (const radio of parentDataEntry.radioContainer.getElementsByTagName(
								"input"
							)) {
								radio.checked = false;
							}
							const info = this.socket.optionalInfo;
							info.valueOptions.forEach((option, i) => {
								option.checked = i === 0;
							});
							previousIndex = 0;

							_artMode = false;
							_artModeType = false;
						}
					});
				}

				container.appendChild(defaultContainer);
				let previousIndex = 0;
				for (let i = 0; i < options.length; i++) {
					const option = options[i];
					if (option.name === "default") continue;

					const inputBox = document.createElement("div");

					const labelText = `label_${option.name}_${uuid}`;
					const input = document.createElement("input");
					input.name = `group_${uuid}`;
					input.type = "radio";
					input.id = labelText;

					input.value = option.name;

					const label = document.createElement("label");
					label.htmlFor = labelText;
					const description = document.createTextNode(option.name);
					label.appendChild(description);

					if (option.checked) {
						setTimeout(() => {
							previousIndex = i;
							this.applyModifiers(option);
							input.checked = true;
						}, 0);
					}

					input.addEventListener("change", (e) => {
						const info = this.socket.optionalInfo;
						info.valueOptions[previousIndex].checked = false;
						previousIndex = i;
						info.valueOptions[i].checked = e.target.checked;
						EventAnalytics.artisticModeSelect(info.valueOptions[i].name);
						this.applyModifiers(option);
					});

					inputBox.appendChild(input);
					inputBox.appendChild(label);
					this.radioContainer.appendChild(inputBox);
				}
				this.getValue = () => this?.defaultCheckbox?.checked;
				this.setValue = (artModeParams) => {
					if (artModeParams) {
						if (this.defaultCheckbox) {
							this.defaultCheckbox.checked = artModeParams.artMode;
							this.radioContainer.style.display = "block";
						}
						const radio = this.radioContainer.querySelector(
							`input[value=${artModeParams.artModeType}]`
						);

						for (const value of this.socket.optionalInfo.valueOptions) {
							if (value.name === artModeParams.artModeType) {
								value.checked = true;
								radio.checked = true;
								this.applyModifiers(value);
								break;
							}
						}
					}
				};
				container.appendChild(this.radioContainer);

				return container;
			}

			default:
				console.log("Unknown type of datafiller", type, value, options);
				this.getValue = () => null;
				return null;
		}
	};

	renderEnum(options, input) {
		const keys = [];
		const values = {};
		input.innerHTML = "";

		for (let i = 0; i < options.values.length; i++) {
			const option = document.createElement("option");
			input.appendChild(option);
			let isDisabled = false;
			if (
				options.values[i].hasAccessGroupMap &&
				!(getUserStore()?.group in options.values[i].hasAccessGroupMap)
			) {
				option.disabled = true;
				isDisabled = true;
			}
			if (options.values[i].constructor.name === "String") {
				option.value = `${options.values[i]}`;
				option.textContent = `${options.values[i]}`;
				keys.push(options.values[i]);
				if (isDisabled) continue;
				values[options.values[i]] = i;
			} else {
				option.value = `${options.values[i].value}`;
				option.textContent = `${options.values[i].name}`;
				keys.push(options.values[i].value);
				if (isDisabled) continue;
				values[options.values[i].value] = i;
			}
		}

		return {
			keys,
			values
		};
	}

	applyModifiers(modifiers, isModificationWasEnabled) {
		if (!modifiers) return;
		this.socket.modifySockets(modifiers.inputs, isModificationWasEnabled);
		this.socket.modifySockets(modifiers.params, isModificationWasEnabled);
	}

	domElement() {
		return this.dom.dataEntry;
	}

	getDomElementName() {
		return this.domElementName;
	}
}
